/* eslint-disable react-hooks/exhaustive-deps */
import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import {
    useGetContactDetailsQuery,
    useGetProfileStrengthQuery,
    useSetContactDetailsMutation,
} from "slice/user";
import { validations } from "utils/validations";
import dialCode from "../../../../constants/dialCode.json";
import { InitialState } from "./types";

export const useContactDetails = () => {
    const navigateTo = useNavigate();
    const { user_id } = useAppSelector((state) => state.userAuth),
        { data, isFetching } = useGetContactDetailsQuery(user_id),
        [inputs, setInputs] = useState<InitialState>({
            countryCode1: "+91",
            mobileNumber1: "",
            isDifferentWhatsAppNumber: "no",
            countryCode2: "",
            mobileNumber2: "",
        }),
        [inputErrors, setInputErrors] = useState({
            mobileNumber1: "",
            mobileNumber2: "",
        }),
        [httpMethod, setHttpMethod] = useState("POST"),
        [isModalOpen, setIsModalOpen] = useState(false),
        [
            setContactDetails,
            {
                isLoading,
                data: createUpdateContactsDetailsResponse,
                isSuccess,
                error,
            },
        ] = useSetContactDetailsMutation();
    const { refetch: refetchProfileStrength } = useGetProfileStrengthQuery(
        user_id ?? "",
        {
            skip: !user_id,
        }
    );
    const radioGroupOptions = useMemo(
        () => [
            { label: "No", value: "no" },
            { label: "Yes, I have different number", value: "yes" },
        ],
        []
    );
    const countryCodeOptions = useMemo(
        () =>
            dialCode.map((code) => ({
                key: `${code.code}`,
                label: `${code.flag} ${code.dial_code}`,
                value: `${code.dial_code}`,
            })),
        []
    );
    const toggleModal = () => setIsModalOpen((prev) => !prev);

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = event.target;
            setInputs((prevValues) => ({
                ...prevValues,
                [name]: value,
            }));

            if (name === "mobileNumber1" || name === "mobileNumber2") {
                setInputErrors((prev) => ({
                    ...prev,
                    [name]: validations(name, value),
                }));
            }
        },
        []
    );

    const handleCountryCodeChange = useCallback(
        (name: string, value: string) => {
            setInputs((prevValues) => ({
                ...prevValues,
                [name]: value,
            }));
        },
        []
    );

    const handleBack = () => {
        navigateTo(
            routeConstants.profile.editProfile.root +
                routeConstants.profile.editProfile.locationAndLanguages
        );
    };

    const onSubmit = useCallback(() => {
        setContactDetails({
            code: inputs.countryCode1,
            mobile_number: inputs.mobileNumber1,
            is_whatsapp_different:
                inputs.isDifferentWhatsAppNumber === "yes" ? true : false,
            whatsapp_number: inputs.mobileNumber2,
            whatsapp_code: inputs.countryCode2,
            user_id,
            method: httpMethod,
        }).then(() => {
            refetchProfileStrength();
        });
    }, [
        httpMethod,
        inputs.countryCode1,
        inputs.countryCode2,
        inputs.isDifferentWhatsAppNumber,
        inputs.mobileNumber1,
        inputs.mobileNumber2,
        setContactDetails,
        user_id,
    ]);

    const handleEditButtonClick = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setHttpMethod("PUT");
    }, []);

    useEffect(() => {
        if (isSuccess) {
            addToast({
                type: "success",
                text: "Data saved successfully!",
            });
            navigateTo(routeConstants.profile.root);
        }
    }, [isSuccess, navigateTo]);

    interface IError {
        status: number;
        data: {
            code?: string[];
            mobile_number?: string[];
            is_whatsapp_different?: string[];
            whatsapp_number?: string[];
            whatsapp_code?: string[];
        };
    }

    useEffect(() => {
        if (error && (error as IError)?.data) {
            const errorMessages = (error as IError).data;

            setInputErrors({
                mobileNumber1: errorMessages.mobile_number
                    ? errorMessages.mobile_number[0]
                    : "",
                mobileNumber2: errorMessages.whatsapp_number
                    ? errorMessages.whatsapp_number[0]
                    : "",
            });
        }
    }, [error]);

    useEffect(() => {
        if (data) {
            setInputs({
                countryCode1: data.code,
                mobileNumber1: data.mobile_number,
                isDifferentWhatsAppNumber: data.is_whatsapp_different
                    ? "yes"
                    : "no",
                countryCode2: data.whatsapp_code,
                mobileNumber2: data.whatsapp_number,
            });
            setHttpMethod("PUT");
        }
    }, [data]);

    useEffect(() => {
        if (createUpdateContactsDetailsResponse) {
            setHttpMethod("PUT");
        }
    }, [createUpdateContactsDetailsResponse]);

    return {
        radioGroupOptions,
        countryCodeOptions,
        inputErrors,
        inputs,
        handleInputChange,
        handleCountryCodeChange,
        onSubmit,
        isLoading,
        handleEditButtonClick,
        isFetching,
        handleBack,
        isModalOpen,
        toggleModal,
    };
};
